import { nflTeamNames, nbaTeamNames, mlbTeamNames } from './team-name-to-abbrev'

const LOOKUP_URI_BASE = 'https://d18d41pmnj8qe1.cloudfront.net/sports'

const NFL_TEAM_LOGOS = {
  'ARZ': 'ARI_cardinals-logo.svg',
  'ARI': 'ARI_cardinals-logo.svg',
  'ATL': 'ATL_falcons-logo.svg',
  'BLT': 'BAL_ravens-logo.svg',
  'BAL': 'BAL_ravens-logo.svg',
  'BUF': 'BUF_bills-logo.svg',
  'CAR': 'CAR_panthers-logo.svg',
  'CHI': 'CHI_bears-logo.svg',
  'CIN': 'CIN_bengals-logo.svg',
  'CLE': 'CLE_browns-logo.svg',
  'DAL': 'DAL_cowboys-logo.svg',
  'DEN': 'DEN_broncos-logo.svg',
  'DET': 'DET_lions-logo.svg',
  'GB': 'GB_packers-logo.svg',
  'HOU': 'HOU_texans-logo.svg',
  'HST': 'HOU_texans-logo.svg',
  'IND': 'IND_colts-logo.svg',
  'JAC': 'JAX_jaguars-logo.svg',
  'JAX': 'JAX_jaguars-logo.svg',
  'KC': 'KC_chiefs-logo.svg',
  'LAR': 'LA_rams-logo.svg',
  'LA': 'LA_rams-logo.svg',
  'LAC': 'LAC_chargers-logo.svg',
  'LV': 'LV_raiders-logo.svg',
  'OAK': 'LV_raiders-logo.svg',
  'MIA': 'MIA_dolphins-logo.svg',
  'MIN': 'MIN_vikings-logo.svg',
  'NE': 'NE_patriots-logo.svg',
  'NO': 'NO_saints-logo.svg',
  'NYG': 'NYG_giants-logo.svg',
  'NYJ': 'NYJ_jets-logo.svg',
  'PHI': 'PHI_eagles-logo.svg',
  'PIT': 'PIT_steelers-logo.svg',
  'SEA': 'SEA_seahawks-logo.svg',
  'SF': 'SF_49ers-logo.svg',
  'TB': 'TB_buccaneers-logo.svg',
  'TEN': 'TEN_titans-logo.svg',
  'WAS': 'WAS_commanders-logo.svg',
}

const MLB_TEAM_LOGOS = {
  'ARI': 'ARI_diamondbacks-logo.svg',
  'AZ': 'ARI_diamondbacks-logo.svg',
  'ATL': 'ATL_braves-logo.svg',
  'BAL': 'BAL_orioles-logo.svg',
  'BOS': 'BOS_redsox-logo.svg',
  'CHC': 'CHI_cubs-logo.svg',
  'CHI': 'CHI_cubs-logo.svg',
  'CWS': 'CWS_whitesox-logo.svg',
  'CIN': 'CIN_reds-logo.svg',
  'CLE': 'CLE_guardians-logo.svg',
  'CLG': 'CLE_guardians-logo.svg',
  'COL': 'COL_rockies-logo.svg',
  'CO': 'COL_rockies-logo.svg',
  'DET': 'DET_tigers-logo.svg',
  'HOU': 'HOU_astros-logo.svg',
  'KC': 'KC_royals-logo.svg',
  'KCR': 'KC_royals-logo.svg',
  'LAA': 'LAA_angels-logo.svg',
  'LAD': 'LA_dodgers-logo.svg',
  'MIA': 'MIA_marlins-logo.svg',
  'MIL': 'MIL_brewers-logo.svg',
  'MIN': 'MIN_twins-logo.svg',
  'NYM': 'NYM_mets-logo.svg',
  'NYY': 'NYY_yankees-logo.svg',
  'OAK': 'OAK_athletics-logo.svg',
  'PHI': 'PHI_phillies-logo.svg',
  'PIT': 'PIT_pirates-logo.svg',
  'SD': 'SD_padres-logo.svg',
  'SF': 'SF_giants-logo.svg',
  'SEA': 'SEA_mariners-logo.svg',
  'STL': 'STL_cardinals-logo.svg',
  'SLC': 'STL_cardinals-logo.svg',
  'TB': 'TB_rays-logo.svg',
  'TEX': 'TEX_rangers-logo.svg',
  'TX': 'TEX_rangers-logo.svg',
  'TOR': 'TOR_bluejays-logo.svg',
  'WAS': 'WSH_nationals-logo.svg',
  'WSH': 'WSH_nationals-logo.svg',
  'DC': 'WSH_nationals-logo.svg',
}

const TEAM_LOGO_MAPS = {
  nfl: NFL_TEAM_LOGOS,
  mlb: MLB_TEAM_LOGOS,
}

const NICKNAME_TO_ABBR = {
  nfl: nflTeamNames,
  mlb: mlbTeamNames,
  nba: nbaTeamNames,
}

// Multi-word MLB names that cannot be reduced to the last token
const MLB_SPECIAL_SLUGS = {
  'Chicago White Sox': 'whitesox',
  'Boston Red Sox': 'redsox',
  'Toronto Blue Jays': 'bluejays',
  'White Sox': 'whitesox',
  'Red Sox': 'redsox',
  'Blue Jays': 'bluejays',
}

const ABBR_TO_NICKNAME = {
  nfl: Object.fromEntries(Object.entries(nflTeamNames).map(([nick, abbr]) => [abbr, nick])),
  mlb: Object.fromEntries(Object.entries(mlbTeamNames).map(([nick, abbr]) => [abbr, nick])),
  nba: Object.fromEntries(Object.entries(nbaTeamNames).map(([nick, abbr]) => [abbr, nick])),
}

const normalizeTeam = (team) => String(team || '').trim().replace(/^@+/, '')

/** Legacy /opt slug used by this app for NBA/MLB before the standardized CDN rename. */
const getLegacyOptSlug = (sport, team) => {
  const sportKey = sport.toLowerCase()
  let name = normalizeTeam(team)
  if (!name) return null

  if (sportKey === 'mlb' && MLB_SPECIAL_SLUGS[name]) {
    return `${MLB_SPECIAL_SLUGS[name]}-logo.svg`
  }

  // Abbrev → nickname for opt filenames (e.g. LAL → lakers-logo.svg)
  const upper = name.toUpperCase()
  const nickFromAbbr = ABBR_TO_NICKNAME[sportKey] && ABBR_TO_NICKNAME[sportKey][upper]
  if (nickFromAbbr) {
    if (sportKey === 'mlb' && MLB_SPECIAL_SLUGS[nickFromAbbr]) {
      return `${MLB_SPECIAL_SLUGS[nickFromAbbr]}-logo.svg`
    }
    name = nickFromAbbr
  }

  // Full names → last token (Los Angeles Lakers → Lakers)
  if (name.includes(' ')) {
    if (sportKey === 'mlb' && MLB_SPECIAL_SLUGS[name]) {
      return `${MLB_SPECIAL_SLUGS[name]}-logo.svg`
    }
    name = name.split(' ').pop()
  }

  return `${name.toLowerCase()}-logo.svg`
}

/** Returns CDN filename for NFL/MLB abbrevs, matching other FTN tools. */
export const getTeamLogoFilename = (team, sport = 'nfl') => {
  if (!team || !sport) return null

  const sportKey = sport.toLowerCase()
  const logoMap = TEAM_LOGO_MAPS[sportKey]
  if (!logoMap) return null

  const normalized = normalizeTeam(team)
  if (!normalized) return null

  if (logoMap[normalized]) return logoMap[normalized]

  const upper = normalized.toUpperCase()
  if (logoMap[upper]) return logoMap[upper]

  if (sportKey === 'mlb') {
    if (normalized === 'Chicago White Sox' || normalized === 'White Sox') return logoMap.CWS
    if (normalized === 'Boston Red Sox' || normalized === 'Red Sox') return logoMap.BOS
    if (normalized === 'Toronto Blue Jays' || normalized === 'Blue Jays') return logoMap.TOR
  }

  const nickMap = NICKNAME_TO_ABBR[sportKey]
  if (nickMap) {
    if (nickMap[normalized] && logoMap[nickMap[normalized]]) {
      return logoMap[nickMap[normalized]]
    }

    // Last token of full name (New York Yankees → Yankees)
    if (normalized.includes(' ')) {
      const last = normalized.split(' ').pop()
      if (nickMap[last] && logoMap[nickMap[last]]) {
        return logoMap[nickMap[last]]
      }
    }

    const titled = normalized.charAt(0).toUpperCase() + normalized.slice(1)
    if (nickMap[titled] && logoMap[nickMap[titled]]) {
      return logoMap[nickMap[titled]]
    }
  }

  return null
}

export const getTeamLogoUrl = (sport, team) => {
  if (!team || !sport) return null

  const sportKey = sport.toLowerCase()

  // NFL + MLB use the standardized CDN filenames shared with other FTN tools.
  // NBA still lives under the legacy /opt/{nickname}-logo.svg paths on this CDN.
  if (sportKey === 'nfl' || sportKey === 'mlb') {
    const filename = getTeamLogoFilename(team, sport)
    if (filename) {
      return `${LOOKUP_URI_BASE}/${sportKey}/logo/${filename}`
    }
  }

  const optSlug = getLegacyOptSlug(sportKey, team)
  if (!optSlug) return null
  return `${LOOKUP_URI_BASE}/${sportKey}/logo/opt/${optSlug}`
}

const IMG_TAG_CACHE = {}

export const getTeamImg = (sport, teamName, size = '28px') => {
  if (!teamName || !sport) return null

  const url = getTeamLogoUrl(sport, teamName)
  if (!url) return null

  const cacheKey = `${sport}:${normalizeTeam(teamName)}`
  if (!IMG_TAG_CACHE[size] || !IMG_TAG_CACHE[size][cacheKey]) {
    if (!IMG_TAG_CACHE[size]) {
      IMG_TAG_CACHE[size] = {}
    }

    IMG_TAG_CACHE[size][cacheKey] = (
      <img src={url} style={{ width: size, verticalAlign: 'middle' }} alt={teamName} />
    )
  }

  return IMG_TAG_CACHE[size][cacheKey]
}

const LOOKUP_SITE_URI_BASE = 'https://d18d41pmnj8qe1.cloudfront.net/logos'
const SITE_IMG_TAG = {}

export const getSiteImg = (site) => {
  if (!SITE_IMG_TAG[site]) {
    SITE_IMG_TAG[site] = (
      <img src={`${LOOKUP_SITE_URI_BASE}/${site.toLowerCase()}.png`} style={{ width: '20px', paddingTop: '5px' }} alt={site} />
    )
  }

  return SITE_IMG_TAG[site]
}

const SPORT_IMG_TAG = {}

export const getSportImg = (sport) => {
  if (!SPORT_IMG_TAG[sport]) {
    SPORT_IMG_TAG[sport] = (
      <img src={`${LOOKUP_SITE_URI_BASE}/${sport.toLowerCase()}-logo.png`} style={{ height: '30px', marginRight: '8px' }} alt={sport} />
    )
  }

  return SPORT_IMG_TAG[sport]
}

let ANCHOR_IMG

export const getAnchorImg = () => {
  if (!ANCHOR_IMG) {
    ANCHOR_IMG = (
      <img src={`${LOOKUP_SITE_URI_BASE}/anchor.svg`} style={{ width: '28px' }} alt="anchor" />
    )
  }

  return ANCHOR_IMG
}
